/**
 * Global customer auth interceptor.
 *
 * SECURITY (2026-09 cookie migration): customer access/refresh tokens now
 * live in httpOnly cookies, never in localStorage/JS. This interceptor can
 * no longer match a request by comparing an Authorization header to a
 * stored token — instead it refreshes on any 401 from a customer-facing
 * URL (not /admin/*, which the admin interceptor owns, and not any call
 * that carried an Authorization header) by calling POST /api/auth/refresh
 * (cookie-only, no body) and retrying once.
 *
 * Call `installCustomerAuthInterceptor()` exactly once at app startup
 * from App.js.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { CustomerStorage } from '../utils/storage';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_URL = `${API}/api/auth/refresh`;

// Dedup concurrent refresh attempts so parallel 401s trigger one refresh.
let refreshPromise = null;

const redirectToCustomerLogin = () => {
  // Don't bounce if we're already on the auth page, or the admin area
  // (admin interceptor is responsible for that surface).
  const path = window.location.pathname;
  if (path.startsWith('/auth') || path.startsWith('/admin')) return;
  toast.error('Your session expired. Please log in again.');
  window.location.href = '/auth';
};

const runRefresh = async () => {
  // `_skipAuthRefresh` stops this call from recursing through the
  // interceptor on its own 401. Cookie carries the refresh token.
  const res = await axios.post(
    REFRESH_URL,
    {},
    { withCredentials: true, _skipAuthRefresh: true }
  );
  return res.data;
};

export const installCustomerAuthInterceptor = () => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;

      // Not a 401, no config, already retried, or explicitly skipped
      if (
        !response ||
        response.status !== 401 ||
        !config ||
        config._customerRetried ||
        config._skipAuthRefresh
      ) {
        return Promise.reject(error);
      }

      // Don't recurse on the refresh endpoint itself
      if (config.url && config.url.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      // This interceptor only owns customer-facing calls. Admin calls
      // (still Bearer + sessionStorage) are handled by adminAuthInterceptor;
      // skip anything that carried an admin Authorization header or hit an
      // /admin/ path so the two interceptors never fight over the same 401.
      const url = config.url || '';
      const sentAuth = (config.headers && config.headers.Authorization) || '';
      if (url.includes('/admin/') || sentAuth) {
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = runRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        await refreshPromise;

        // Retry the original request — refreshed cookies are attached
        // automatically by the browser.
        config._customerRetried = true;
        config.withCredentials = true;
        return axios(config);
      } catch (refreshErr) {
        CustomerStorage.clear();
        redirectToCustomerLogin();
        return Promise.reject(error);
      }
    }
  );
};

export default installCustomerAuthInterceptor;
